import { logDev } from '../utils/LogDev';

export interface Color {
    r: number;
    g: number;
    b: number;
}

export interface HSL {
    h: number;
    s: number;
    l: number;
}
export class ColorPicker {
    private container: HTMLElement;
    private resizeHandler: (() => void) | null = null;
    private fullPalette: string[] = []; // Stores 24 pages * 16 colors = 384 colors
    private currentColor: string = '#000000';
    private selectedPaletteIndex: number = -1;
    private onColorChange: (color: string) => void = () => { };
    public onPaletteChange: (palette: string[]) => void = () => { };
    public onEyeDropperFallback?: () => void;
    private onPickerClose: () => void = () => { };
    private t: (key: any) => string;
    private activeTrigger: HTMLElement | null = null;

    private hue: number = 0;
    private saturation: number = 0;
    private lightness: number = 0;
    private alpha: number = 1;

    private currentPalettePage: number = 0;

    private elements: {
        picker: HTMLElement;
        canvas: HTMLCanvasElement;
        hueSlider: HTMLInputElement;
        paletteGrid: HTMLElement;
        rgbInputs: HTMLInputElement[];
        hslInputs: HTMLInputElement[];
        hexInput: HTMLInputElement;
        preview: HTMLElement;
        eyeDropperBtn: HTMLButtonElement;
        alphaSlider: HTMLInputElement;
        closeBtn: HTMLButtonElement;
    } | null = null;

    constructor(parent: HTMLElement, t: (key: any) => string) {
        this.container = parent;
        this.t = t;
        this.initPalette();
        this.render();
    }

    public static readonly PALETTE_PAGES: string[][] = [
        // Page 1: Classic (Windows)
        ['#000000', '#808080', '#800000', '#808000', '#008000', '#008080', '#000080', '#800080',
         '#ffffff', '#c0c0c0', '#ff0000', '#ffff00', '#00ff00', '#00ffff', '#0000ff', '#ff00ff'],
        // Page 2: Pastel Dreams
        ['#f8b4b4', '#fcd5ce', '#ffd7ba', '#ffe5b4', '#fff3b0', '#d4edda', '#b5ead7', '#c7ceea',
         '#e2c2f0', '#f0c6d4', '#fadadd', '#f7e1d7', '#dedaf4', '#c1e7e3', '#b3cde0', '#e8d5b7'],
        // Page 3: Neon Lights
        ['#ff0040', '#ff0080', '#ff00bf', '#ff00ff', '#bf00ff', '#8000ff', '#4000ff', '#0000ff',
         '#00ff40', '#00ff80', '#00ffbf', '#00ffff', '#ffff00', '#ff8000', '#ff4000', '#ffffff'],
        // Page 4: Earth Tones
        ['#2d1b00', '#4a2c0a', '#6b3a1f', '#8b5e3c', '#a67c52', '#c19a6b', '#d2b48c', '#e8d5b7',
         '#3e5c3e', '#556b2f', '#6b8e23', '#8fbc8f', '#4a6741', '#7a6e5e', '#a0937d', '#c4b49a'],
        // Page 5: Ocean Depths
        ['#001f3f', '#003366', '#004080', '#005b96', '#0077b6', '#0096c7', '#00b4d8', '#48cae4',
         '#90e0ef', '#ade8f4', '#caf0f8', '#e0fbfc', '#023e8a', '#0077b6', '#00a8e8', '#ffffff'],
        // Page 6: Sunset Blaze
        ['#1a0a2e', '#2d1b69', '#4a1942', '#6b2d5b', '#8b3a62', '#c44536', '#e76f51', '#f4a261',
         '#e9c46a', '#ffd166', '#ffe66d', '#ffef9f', '#ff6b6b', '#ee6c4d', '#f3722c', '#f8961e'],
        // Page 7: Retro Gaming
        ['#000000', '#1d2b53', '#7e2553', '#008751', '#ab5236', '#5f574f', '#c2c3c7', '#fff1e8',
         '#ff004d', '#ffa300', '#ffec27', '#00e436', '#29adff', '#83769c', '#ff77a8', '#ffccaa'],
        // Page 8: Monochrome Pro
        ['#000000', '#111111', '#222222', '#333333', '#444444', '#555555', '#666666', '#777777',
         '#888888', '#999999', '#aaaaaa', '#bbbbbb', '#cccccc', '#dddddd', '#eeeeee', '#ffffff'],
        // Page 9: Autumn Harvest
        ['#2d0a0a', '#5c1a1a', '#8b2500', '#a0522d', '#b8651a', '#cc7722', '#d4943a', '#daa520',
         '#c9b037', '#a8a047', '#6b702a', '#556b2f', '#4a4a2a', '#7a5c3a', '#9e7b4f', '#c4a35a'],
        // Page 10: Candy Pop
        ['#ff1493', '#ff69b4', '#ff85a1', '#fbb1bd', '#ff5e78', '#ff2d55', '#e91e63', '#c2185b',
         '#7b1fa2', '#9c27b0', '#ba68c8', '#e040fb', '#ea80fc', '#ff80ab', '#ff4081', '#f50057'],
        // Page 11: Forest Moss
        ['#0b3d0b', '#1a5c1a', '#2d6a2d', '#3a7d3a', '#228b22', '#32cd32', '#66bb6a', '#81c784',
         '#a5d6a7', '#c8e6c9', '#2e4a2e', '#3e5c3e', '#4e6e4e', '#5c835c', '#6fa36f', '#8fbc8f'],
        // Page 12: Ice Crystal
        ['#e8eaf6', '#c5cae9', '#9fa8da', '#7986cb', '#5c6bc0', '#3f51b5', '#3949ab', '#303f9f',
         '#e1f5fe', '#b3e5fc', '#81d4fa', '#4fc3f7', '#29b6f6', '#03a9f4', '#039be5', '#0288d1'],
        // Page 13: Vintage Sepia
        ['#3c2415', '#5c3a2a', '#7a5240', '#987053', '#b08d6e', '#c4a882', '#d8c49e', '#e8dcc8',
         '#f0e6d3', '#faf3eb', '#6d4c41', '#795548', '#8d6e63', '#a1887f', '#bcaaa4', '#d7ccc8'],
        // Page 14: Tropical Paradise
        ['#00695c', '#00897b', '#009688', '#26a69a', '#4db6ac', '#80cbc4', '#e0f2f1', '#fff9c4',
         '#fff176', '#ffee58', '#ffeb3b', '#ff8f00', '#ff6f00', '#e65100', '#bf360c', '#ffffff'],
        // Page 15: Galaxy Nebula
        ['#0d0221', '#150734', '#1a0a4a', '#240e61', '#2e1278', '#4a148c', '#6a1b9a', '#8e24aa',
         '#ab47bc', '#ce93d8', '#e1bee7', '#f3e5f5', '#1a237e', '#283593', '#3949ab', '#e8eaf6'],
        // Page 16: Cyberpunk
        ['#0a0a0a', '#1a1a2e', '#16213e', '#0f3460', '#e94560', '#ff2e63', '#ff6b9d', '#c70039',
         '#00fff5', '#00d4ff', '#0099ff', '#7209b7', '#b5179e', '#f72585', '#fca311', '#ffffff'],
        // Pages 17-24: Empty Palettes (transparent / black)
        Array(16).fill('#00000000'),
        Array(16).fill('#00000000'),
        Array(16).fill('#00000000'),
        Array(16).fill('#00000000'),
        Array(16).fill('#00000000'),
        Array(16).fill('#00000000'),
        Array(16).fill('#00000000'),
        Array(16).fill('#00000000')
    ];

    public static get DEFAULT_PALETTE(): string[] {
        return ColorPicker.PALETTE_PAGES.flat();
    }

    private initPalette() {
        this.fullPalette = [...ColorPicker.DEFAULT_PALETTE];
    }

    public setPalette(newPalette: string[]) {
        if (!newPalette) return;
        this.fullPalette = newPalette.map(c => c.replace(/\s/g, '') === 'rgba(0,0,0,0)' ? '#00000000' : c);
        
        // Pad to exactly 384 colors if necessary, to support old sprites with 16-color palettes
        const expectedLength = ColorPicker.PALETTE_PAGES.length * 16;
        if (this.fullPalette.length < expectedLength) {
            const currentLen = this.fullPalette.length;
            const defaultFlat = ColorPicker.DEFAULT_PALETTE;
            this.fullPalette.push(...defaultFlat.slice(currentLen, expectedLength));
        } else if (this.fullPalette.length > expectedLength) {
            this.fullPalette = this.fullPalette.slice(0, expectedLength);
        }
        
        this.updatePaletteUI();
    }

    public getPalette() {
        return this.fullPalette;
    }

    public setColor(hex: string) {
        if (hex.replace(/\s/g, '') === 'rgba(0,0,0,0)') {
            hex = '#00000000';
        }
        this.currentColor = hex;
        const rgba = this.hexToRgba(hex);
        const hsl = this.rgbToHsl(rgba.r, rgba.g, rgba.b);
        this.hue = hsl.h;
        this.saturation = hsl.s;
        this.lightness = hsl.l;
        this.alpha = rgba.a;
        this.updateUI();
    }

    private render() {
        const picker = document.createElement('div');
        picker.className = 'sl-color-picker-popover';
        picker.id = 'sl-color-picker';
        picker.style.display = 'none';

        picker.innerHTML = `
            <div class="sl-cp-header">
                <span class="sl-cp-title">${this.t('label_colors')}</span>
                <button id="sl-cp-close" class="sl-cp-close-btn" title="${this.t('action_cancel')}">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
            </div>
            <div class="sl-cp-body">
                <div class="sl-cp-left">
                    <div class="sl-cp-palette-row">
                        <div class="sl-cp-palette" id="sl-cp-palette"></div>
                        <div class="sl-cp-palette-nav">
                            <button id="sl-cp-palette-prev" class="sl-cp-palette-nav-btn" title="Previous palette">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
                            </button>
                            <span id="sl-cp-palette-indicator" class="sl-cp-palette-indicator">1 / 16</span>
                            <button id="sl-cp-palette-next" class="sl-cp-palette-nav-btn" title="Next palette">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                            </button>
                        </div>
                    </div>
                    <div class="sl-cp-model-grid">
                        <div class="sl-cp-model-group">
                            <label>RGB</label>
                            <input type="number" data-model="rgb" data-idx="0" min="0" max="255">
                            <input type="number" data-model="rgb" data-idx="1" min="0" max="255">
                            <input type="number" data-model="rgb" data-idx="2" min="0" max="255">
                        </div>
                        <div class="sl-cp-model-group">
                            <label>HSL</label>
                            <input type="number" data-model="hsl" data-idx="0" min="0" max="360">
                            <input type="number" data-model="hsl" data-idx="1" min="0" max="100">
                            <input type="number" data-model="hsl" data-idx="2" min="0" max="100">
                        </div>
                    </div>
                </div>
                <div class="sl-cp-right">
                    <div class="sl-cp-visual">
                        <div class="sl-cp-canvas-row">
                            <div class="sl-cp-canvas-container">
                                <canvas id="sl-cp-canvas" width="216" height="150"></canvas>
                                <div id="sl-cp-marker" class="sl-cp-marker"></div>
                            </div>
                            <div class="sl-cp-alpha-container sl-chessboard">
                                <input type="range" id="sl-cp-alpha" class="sl-cp-alpha-slider" min="0" max="100" value="100" orient="vertical">
                            </div>
                        </div>
                        <input type="range" id="sl-cp-hue" class="sl-cp-hue-slider" min="0" max="360" value="0">
                    </div>
                    <div class="sl-cp-preview-row">
                        <div class="sl-cp-preview sl-chessboard">
                            <div id="sl-cp-preview" style="width:100%; height:100%"></div>
                        </div>
                        <button id="sl-cp-eyedropper" class="sl-cp-btn" title="Eyedropper">
                            <i class="fa-solid fa-eye-dropper"></i>
                        </button>
                        <input type="text" id="sl-cp-hex" class="sl-cp-hex-input" value="#000000">
                    </div>
                </div>
            </div>
        `;

        this.container.appendChild(picker);

        this.elements = {
            picker,
            canvas: picker.querySelector('#sl-cp-canvas') as HTMLCanvasElement,
            hueSlider: picker.querySelector('#sl-cp-hue') as HTMLInputElement,
            paletteGrid: picker.querySelector('#sl-cp-palette') as HTMLElement,
            rgbInputs: Array.from(picker.querySelectorAll('input[data-model="rgb"]')) as HTMLInputElement[],
            hslInputs: Array.from(picker.querySelectorAll('input[data-model="hsl"]')) as HTMLInputElement[],
            hexInput: picker.querySelector('#sl-cp-hex') as HTMLInputElement,
            preview: picker.querySelector('#sl-cp-preview') as HTMLElement,
            eyeDropperBtn: picker.querySelector('#sl-cp-eyedropper') as HTMLButtonElement,
            alphaSlider: picker.querySelector('#sl-cp-alpha') as HTMLInputElement,
            closeBtn: picker.querySelector('#sl-cp-close') as HTMLButtonElement
        };

        // Setup palette navigation
        const prevBtn = picker.querySelector('#sl-cp-palette-prev') as HTMLButtonElement;
        const nextBtn = picker.querySelector('#sl-cp-palette-next') as HTMLButtonElement;
        prevBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigatePalettePage(-1);
        });
        nextBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigatePalettePage(1);
        });

        this.setupEvents();
        this.updatePaletteUI();

        // Clamp picker to viewport on window resize
        this.resizeHandler = () => {
            if (this.isOpen()) {
                this.clampToViewport();
            }
        };
        window.addEventListener('resize', this.resizeHandler);
    }

    private setupEvents() {
        if (!this.elements) return;

        const { canvas, hueSlider, eyeDropperBtn, hexInput } = this.elements;

        hueSlider.addEventListener('input', () => {
            this.hue = parseInt(hueSlider.value);
            this.updateFromHsl();
        });

        this.elements.alphaSlider.addEventListener('input', () => {
            this.alpha = parseInt(this.elements!.alphaSlider.value) / 100;
            this.updateFromHsl();
        });

        const onMouseMove = (e: MouseEvent) => {
            this.handleCanvasInteraction(e);
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            this.triggerChange();
        };

        canvas.addEventListener('mousedown', (e: MouseEvent) => {
            this.handleCanvasInteraction(e);
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        });

        const onTouchMove = (e: TouchEvent) => {
            e.preventDefault(); // Prevent scrolling while dragging
            this.handleCanvasInteraction(e.touches[0]);
        };

        const onTouchEnd = () => {
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
            this.triggerChange();
        };

        canvas.addEventListener('touchstart', (e: TouchEvent) => {
            e.preventDefault(); // Prevent scrolling/zooming while dragging
            this.handleCanvasInteraction(e.touches[0]);
            document.addEventListener('touchmove', onTouchMove, { passive: false });
            document.addEventListener('touchend', onTouchEnd);
        }, { passive: false });

        eyeDropperBtn.addEventListener('click', () => {
            if (this.onEyeDropperFallback) {
                this.onEyeDropperFallback();
            } else {
                alert('Eyedropper not configured.');
            }
        });

        hexInput.addEventListener('change', () => {
            let val = hexInput.value;
            if (!val.startsWith('#')) val = '#' + val;
            if (/^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(val)) {
                this.setColor(val);
                this.triggerChange();
            } else {
                hexInput.value = this.currentColor;
            }
        });

        this.elements.rgbInputs.forEach(input => {
            input.addEventListener('input', () => this.updateFromInputs('rgb'));
        });
        this.elements.hslInputs.forEach(input => {
            input.addEventListener('input', () => this.updateFromInputs('hsl'));
        });

        this.elements.closeBtn.addEventListener('click', () => {
            this.close();
        });

        // Unselect palette on background click
        this.elements.picker.addEventListener('mousedown', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const backgroundClasses = ['sl-color-picker-popover', 'sl-cp-main', 'sl-cp-inputs', 'sl-cp-visual', 'sl-cp-model-grid', 'sl-cp-header'];
            if (backgroundClasses.some(cls => target.classList.contains(cls)) || target === this.elements!.picker) {
                if (this.selectedPaletteIndex !== -1) {
                    this.selectedPaletteIndex = -1;
                    this.updatePaletteUI();
                }
            }
        });

        // Click outside to close handled by LifeCycleManager
    }

    private handleCanvasInteraction(e: MouseEvent | Touch) {
        if (!this.elements) return;
        const rect = this.elements.canvas.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

        const h = this.hue;
        const s_hsv = x;
        const v_hsv = 1 - y;

        const hsl = this.hsvToHsl(h, s_hsv, v_hsv);
        this.saturation = hsl.s;
        this.lightness = hsl.l;

        this.updateFromHsl();
    }

    private updateFromHsl() {
        const rgb = this.hslToRgb(this.hue, this.saturation, this.lightness);
        this.currentColor = this.rgbaToHex(rgb.r, rgb.g, rgb.b, this.alpha);
        this.updateUI();
        this.triggerChange();
    }

    private updateFromInputs(model: 'rgb' | 'hsl') {
        if (!this.elements) return;
        if (model === 'rgb') {
            const r = parseInt(this.elements.rgbInputs[0].value) || 0;
            const g = parseInt(this.elements.rgbInputs[1].value) || 0;
            const b = parseInt(this.elements.rgbInputs[2].value) || 0;
            this.setColor(this.rgbaToHex(r, g, b, this.alpha));
            this.triggerChange();
        } else if (model === 'hsl') {
            this.hue = parseInt(this.elements.hslInputs[0].value) || 0;
            this.saturation = parseInt(this.elements.hslInputs[1].value) || 0;
            this.lightness = parseInt(this.elements.hslInputs[2].value) || 0;
            const rgb = this.hslToRgb(this.hue, this.saturation, this.lightness);
            this.currentColor = this.rgbaToHex(rgb.r, rgb.g, rgb.b, this.alpha);
            this.updateUI();
            this.triggerChange();
        }
    }

    private updateUI() {
        if (!this.elements) return;
        const { preview, hexInput, rgbInputs, hslInputs } = this.elements;

        preview.style.backgroundColor = this.currentColor;
        this.elements.alphaSlider.value = Math.round(this.alpha * 100).toString();
        this.elements.hueSlider.value = Math.round(this.hue).toString();
        hexInput.value = this.currentColor.toUpperCase();

        const rgba = this.hexToRgba(this.currentColor);
        rgbInputs[0].value = rgba.r.toString();
        rgbInputs[1].value = rgba.g.toString();
        rgbInputs[2].value = rgba.b.toString();

        hslInputs[0].value = Math.round(this.hue).toString();
        hslInputs[1].value = Math.round(this.saturation).toString();
        hslInputs[2].value = Math.round(this.lightness).toString();


        this.drawCanvas();
        this.updateMarker();

        const rgb = this.hexToRgba(this.currentColor);
        const opaqueHex = this.rgbaToHex(rgb.r, rgb.g, rgb.b, 1);
        this.elements.alphaSlider.style.background = `linear-gradient(to right, transparent, ${opaqueHex})`;
    }

    private updatePaletteUI() {
        if (!this.elements) return;
        this.elements.paletteGrid.innerHTML = '';
        
        const startIndex = this.currentPalettePage * 16;
        const currentPageColors = this.fullPalette.slice(startIndex, startIndex + 16);

        currentPageColors.forEach((color, i) => {
            const globalIndex = startIndex + i;
            const slot = document.createElement('div');
            slot.className = 'sl-cp-palette-slot';
            if (this.selectedPaletteIndex === globalIndex) {
                slot.classList.add('selected');
            }
            slot.style.backgroundColor = color;
            slot.title = `Color ${globalIndex + 1} (Right click to set)`;
            slot.onclick = (e) => {
                e.stopPropagation();
                if (this.selectedPaletteIndex === globalIndex) {
                    this.selectedPaletteIndex = -1;
                } else {
                    this.selectedPaletteIndex = globalIndex;
                    this.setColor(color);
                    this.triggerChange();
                }
                this.updatePaletteUI();
            };
            slot.oncontextmenu = (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.fullPalette[globalIndex] = this.currentColor;
                this.selectedPaletteIndex = globalIndex;
                this.updatePaletteUI();
                this.onPaletteChange(this.fullPalette);
            };

            // Add chessboard and color inner div
            slot.classList.add('sl-chessboard');
            const colorDiv = document.createElement('div');
            colorDiv.style.width = '100%';
            colorDiv.style.height = '100%';
            colorDiv.style.backgroundColor = color;
            slot.appendChild(colorDiv);

            this.elements!.paletteGrid.appendChild(slot);
        });

        // Update page indicator
        const indicator = this.elements.picker.querySelector('#sl-cp-palette-indicator');
        if (indicator) {
            indicator.textContent = `${this.currentPalettePage + 1} / ${ColorPicker.PALETTE_PAGES.length}`;
        }
    }

    private navigatePalettePage(direction: number) {
        const totalPages = ColorPicker.PALETTE_PAGES.length;
        this.currentPalettePage = (this.currentPalettePage + direction + totalPages) % totalPages;
        this.selectedPaletteIndex = -1;
        this.updatePaletteUI();
    }

    private drawCanvas() {
        if (!this.elements) return;
        const ctx = this.elements.canvas.getContext('2d');
        if (!ctx) return;
        const w = this.elements.canvas.width;
        const h = this.elements.canvas.height;

        ctx.clearRect(0, 0, w, h);

        // Gradient for Saturation (left to right)
        const gS = ctx.createLinearGradient(0, 0, w, 0);
        gS.addColorStop(0, '#fff');
        gS.addColorStop(1, `hsl(${this.hue}, 100%, 50%)`);
        ctx.fillStyle = gS;
        ctx.fillRect(0, 0, w, h);

        // Gradient for Lightness (bottom to top)
        const gL = ctx.createLinearGradient(0, 0, 0, h);
        gL.addColorStop(0, 'rgba(0,0,0,0)');
        gL.addColorStop(1, '#000');
        ctx.fillStyle = gL;
        ctx.fillRect(0, 0, w, h);
    }

    private updateMarker() {
        if (!this.elements) return;
        const marker = this.elements.picker.querySelector('#sl-cp-marker') as HTMLElement;
        if (!marker) return;

        // HSL square logic
        const canvas = this.elements.canvas;
        const rect = canvas.getBoundingClientRect();
        const containerRect = canvas.parentElement!.getBoundingClientRect();

        const offsetX = rect.left - containerRect.left;
        const offsetY = rect.top - containerRect.top;

        // Convert HSL state to HSV for UI positioning
        const hsv = this.hslToHsv(this.hue, this.saturation / 100, this.lightness / 100);

        const x = (hsv.s / 100) * rect.width + offsetX;
        const y = (1 - (hsv.v / 100)) * rect.height + offsetY;

        marker.style.left = `${x}px`;
        marker.style.top = `${y}px`;
    }

    public open(trigger: HTMLElement, x: number, y: number, initialColor: string, onChange: (c: string) => void, onClose?: () => void) {
        if (!this.elements) return;
        this.activeTrigger = trigger;
        this.elements.picker.style.display = 'block';

        // Reset all positioning to use left/top (clear any right set by drag)
        this.elements.picker.style.left = `${x}px`;
        this.elements.picker.style.top = `${y}px`;
        this.elements.picker.style.right = 'auto';
        this.elements.picker.style.bottom = 'auto';

        this.currentColor = initialColor;
        this.setColor(initialColor);
        this.onColorChange = onChange;
        this.onPickerClose = onClose || (() => { });

        // --- Refined Positioning Logic ---
        const rect = this.elements.picker.getBoundingClientRect();
        const screenW = window.innerWidth;
        const screenH = window.innerHeight;

        // 1. Horizontal: Pin to right edge if it overflows, or if it's generally in the right half
        if (x + rect.width > screenW - 10) {
            this.elements.picker.style.left = `${screenW - rect.width - 10}px`;
        } else if (x > screenW / 2) {
            // If we are in the right half, align right edge with trigger right edge if possible
            this.elements.picker.style.left = `${Math.max(10, x - rect.width + 30)}px`;
        }

        // 2. Vertical: Flip if no space at bottom
        if (y + rect.height > screenH - 10) {
            // Open ABOVE the trigger (assume trigger is around 'y' height)
            // Subtracting rect.height + triggerHeight + margin
            this.elements.picker.style.top = `${y - rect.height - 40}px`;
        }

        // 3. Final clamp to viewport
        this.clampToViewport();
    }

    /**
     * Clamps the picker element so it stays fully within the visible viewport.
     * Converts any right-based positioning (set by drag) back to left-based
     * so the clamping math is consistent.
     */
    private clampToViewport() {
        if (!this.elements) return;
        const picker = this.elements.picker;
        const rect = picker.getBoundingClientRect();
        const parentRect = (picker.offsetParent as HTMLElement || document.body).getBoundingClientRect();
        const margin = 10;

        // Work entirely in viewport coords, then convert to parent-relative left/top
        let vpLeft = rect.left;
        let vpTop = rect.top;

        // Clamp right edge
        if (vpLeft + rect.width > window.innerWidth - margin) {
            vpLeft = window.innerWidth - rect.width - margin;
        }
        // Clamp left edge
        if (vpLeft < margin) {
            vpLeft = margin;
        }
        // Clamp bottom edge
        if (vpTop + rect.height > window.innerHeight - margin) {
            vpTop = window.innerHeight - rect.height - margin;
        }
        // Clamp top edge
        if (vpTop < margin) {
            vpTop = margin;
        }

        // Convert viewport coords to parent-relative
        picker.style.left = `${vpLeft - parentRect.left}px`;
        picker.style.top = `${vpTop - parentRect.top}px`;
        picker.style.right = 'auto';
        picker.style.bottom = 'auto';
    }

    public close() {
        if (this.elements && this.elements.picker.style.display === 'block') {
            this.elements.picker.style.display = 'none';
            this.elements.picker.style.opacity = '1';
            this.elements.picker.style.pointerEvents = 'auto';
            this.onPickerClose();
        }
    }

    public hideForPicker() {
        if (this.elements) {
            this.elements.picker.style.opacity = '0';
            this.elements.picker.style.pointerEvents = 'none';
        }
    }

    public isOpen() {
        return this.elements?.picker.style.display === 'block';
    }

    public getActiveTrigger() {
        return this.activeTrigger;
    }

    public getElement() {
        return this.elements?.picker;
    }

    public triggerChange() {
        if (this.selectedPaletteIndex !== -1) {
            this.fullPalette[this.selectedPaletteIndex] = this.currentColor;
            // Update the slot UI specifically to avoid full re-render if possible
            const localIndex = this.selectedPaletteIndex - (this.currentPalettePage * 16);
            if (localIndex >= 0 && localIndex < 16) {
                const slots = this.elements?.paletteGrid.querySelectorAll('.sl-cp-palette-slot');
                if (slots && slots[localIndex]) {
                    const colorDiv = (slots[localIndex] as HTMLElement).querySelector('div');
                    if (colorDiv) {
                        colorDiv.style.backgroundColor = this.currentColor;
                    }
                }
            }
            this.onPaletteChange(this.fullPalette);
        }
        this.onColorChange(this.currentColor);
    }

    // --- Utils ---

    private hexToRgba(hex: string): Color & { a: number } {
        if (hex.length === 4) {
            const r = parseInt(hex[1] + hex[1], 16);
            const g = parseInt(hex[2] + hex[2], 16);
            const b = parseInt(hex[3] + hex[3], 16);
            return { r, g, b, a: 1 };
        }
        if (hex.length === 9) {
            const bigint = parseInt(hex.slice(1), 16);
            return {
                r: (bigint >> 24) & 255,
                g: (bigint >> 16) & 255,
                b: (bigint >> 8) & 255,
                a: (bigint & 255) / 255
            };
        }
        const bigint = parseInt(hex.slice(1), 16);
        return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255, a: 1 };
    }

    private rgbaToHex(r: number, g: number, b: number, a: number): string {
        const f = (x: number) => Math.round(x).toString(16).padStart(2, '0');
        if (a >= 1) {
            return `#${f(r)}${f(g)}${f(b)}`.toUpperCase();
        }
        const alphaHex = f(a * 255);
        return `#${f(r)}${f(g)}${f(b)}${alphaHex}`.toUpperCase();
    }

    private rgbToHsl(r: number, g: number, b: number): HSL {
        r /= 255; g /= 255; b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h = 0, s, l = (max + min) / 2;
        if (max === min) h = s = 0;
        else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
            }
            h /= 6;
        }
        return { h: h * 360, s: s * 100, l: l * 100 };
    }

    private hslToRgb(h: number, s: number, l: number): Color {
        h /= 360; s /= 100; l /= 100;
        let r, g, b;
        if (s === 0) r = g = b = l;
        else {
            const hue2rgb = (p: number, q: number, t: number) => {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1 / 6) return p + (q - p) * 6 * t;
                if (t < 1 / 2) return q;
                if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                return p;
            };
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }
        return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
    }

    private hsvToHsl(h: number, s: number, v: number): HSL {
        const l = v * (1 - s / 2);
        const s_l = (l === 0 || l === 1) ? 0 : (v - l) / Math.min(l, 1 - l);
        return { h, s: s_l * 100, l: l * 100 };
    }

    private hslToHsv(h: number, s: number, l: number): { h: number, s: number, v: number } {
        const v = l + s * Math.min(l, 1 - l);
        const s_v = v === 0 ? 0 : 2 * (1 - l / v);
        return { h, s: s_v * 100, v: v * 100 };
    }

}
